import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import { getInvitation, acceptInvitation, type GetInvitationResponse } from '../services/api';

type TenantUserRole = 'owner' | 'admin' | 'member';

const ROLE_LABELS: Record<TenantUserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

export function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    isLoading: authLoading,
    loginWithIntuit,
    fetchUserTenants,
    selectTenant,
  } = useAuthStore();

  const [invitation, setInvitation] = useState<GetInvitationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Fetch invitation details
  useEffect(() => {
    async function fetchInvitation() {
      if (!token) {
        setError('Invalid invitation link');
        setIsLoading(false);
        return;
      }

      try {
        const data = await getInvitation(token);
        setInvitation(data);
      } catch (err) {
        if (err instanceof Error) {
          if (err.message.includes('404')) {
            setError('This invitation was not found or has expired.');
          } else if (err.message.includes('410')) {
            setError('This invitation has expired or already been used.');
          } else {
            setError(err.message);
          }
        } else {
          setError('Failed to load invitation');
        }
      } finally {
        setIsLoading(false);
      }
    }

    fetchInvitation();
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;

    setIsAccepting(true);
    setAcceptError(null);

    try {
      const result = await acceptInvitation(token);
      // Refresh tenants to include the new one
      await fetchUserTenants();
      // Select the new tenant
      selectTenant(result.tenantId);
      // Navigate to the dashboard
      navigate('/', { replace: true });
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Failed to accept invitation');
      setIsAccepting(false);
    }
  };

  const handleLoginWithEmail = () => {
    // Store the return URL so we can redirect back after login
    sessionStorage.setItem('invitation_return_token', token || '');
    navigate('/login');
  };

  const handleLoginWithIntuit = () => {
    // Store the return URL so we can redirect back after login
    sessionStorage.setItem('invitation_return_token', token || '');
    loginWithIntuit();
  };

  const handleCreateAccount = () => {
    // Navigate to signup with the invitation token
    navigate(`/signup?token=${token}`);
  };

  // Show loading state
  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Show invitation details
  if (!invitation) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
            <svg
              className="h-6 w-6 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">You're Invited!</h1>
          <p className="text-sm text-gray-600 mt-1">
            {invitation.inviterEmail ? `${invitation.inviterEmail} has` : 'You have been'} invited
            you to join
          </p>
        </div>

        {/* Invitation details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 text-center mb-2">
            {invitation.tenantName}
          </h2>
          <p className="text-sm text-gray-600 text-center">
            as a <span className="font-medium">{ROLE_LABELS[invitation.role]}</span>
          </p>
        </div>

        {/* Role description */}
        <p className="text-sm text-gray-500 text-center mb-6">
          {invitation.role === 'admin'
            ? 'As an Admin, you can invite members, manage sync settings, and view the team.'
            : 'As a Member, you can view and upload invoices.'}
        </p>

        {/* Accept error */}
        {acceptError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{acceptError}</p>
          </div>
        )}

        {/* Actions based on auth state */}
        {isAuthenticated ? (
          <button
            onClick={handleAccept}
            disabled={isAccepting}
            className="w-full px-4 py-3 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isAccepting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Accepting...
              </span>
            ) : (
              'Accept Invitation'
            )}
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 text-center mb-4">
              Sign in or create an account to accept this invitation
            </p>

            {/* Create Account - primary option for new users */}
            <button
              onClick={handleCreateAccount}
              className="w-full px-4 py-3 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Create Account
            </button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Or sign in if you have an account
                </span>
              </div>
            </div>

            <button
              onClick={handleLoginWithIntuit}
              className="w-full px-4 py-3 text-sm font-medium text-white bg-[#0077C5] rounded-md hover:bg-[#006BB3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0077C5] flex items-center justify-center gap-2"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
              </svg>
              Sign in with Intuit
            </button>

            <button
              onClick={handleLoginWithEmail}
              className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Sign in with Email
            </button>
          </div>
        )}

        {/* Expiration notice */}
        <p className="mt-4 text-xs text-gray-500 text-center">
          This invitation expires on {invitation.expiresAt.split('T')[0]}
        </p>
      </div>
    </div>
  );
}
